/**
 * Sub-element commands — the direct-manipulation command surface
 * (docs/bim-plan/01-architecture.md §3.1). All resolve NodeRef/EdgeRef against the
 * CURRENT path value at execution time and fail soft (return false/null) when the ref
 * no longer exists. Each writes the whole Path2D back ATOMICALLY in one transaction —
 * and re-anchors hosted openings by WORLD position in that same transaction (2.6
 * polish, Matt's report: hostOffsetFt is arc length from the path start, so a shape
 * edit that changes an EARLIER segment's length used to drag every opening after it).
 */
import * as Y from 'yjs'
import type { CategoryId, ElementId, NodeId, ParamValue, Path2D, PathNode, Pt2 } from '../types'
import { getElement, hostedElements, maps, resolveElement, transact, type ModelDoc } from '../doc'
import { pathParamKeyFor } from '../registry'
import * as ops from '../pathOps'
import { flattenPathPts } from '../arc'
import { edgeAngleInfo } from '../offAxis'
import { normalizeStairRunPath, stairLayoutForPath } from '../stairLayout'
import { stairVerticalExtent, stairStepInputs } from '../stair'
export type { EdgeRef } from '../pathOps'
import type { EdgeRef } from '../pathOps'
import { eaveEditPhaseFor, isRoofEaveEdgeKey } from '../eavePhase'
import { normalizePhaseChanges, withEdgeChange, UNSET, PHASE_CHANGES_KEY } from '../phaseChanges'
import { changePhaseFor } from './elements'
import { moveBodyParts } from './body'
import { chairsOfTable, isDiningTable } from './diningChairs'

/** Same probe `translateElement` already used — the registry's declared plan-geometry
 *  param, falling back to a bare `path`/`boundary` for un-registered/anchor categories
 *  (object, column: a single-node `path` with no `pathParamKey` declared). */
function geomKeyFor(pm: Y.Map<unknown>, category: CategoryId | undefined): string | null {
  const declared = pathParamKeyFor(category as CategoryId)
  if (declared && pm.get(declared)) return declared
  if (pm.get('path')) return 'path'
  if (pm.get('boundary')) return 'boundary'
  return null
}

function readPath(model: ModelDoc, elementId: ElementId, paramKey: string): { pm: Y.Map<unknown>; path: Path2D } | null {
  const em = maps(model).elements.get(elementId)
  if (!em) return null
  const pm = em.get('params') as Y.Map<unknown> | undefined
  if (!pm) return null
  // MODIFY PHASE-2 (Pin): a pinned element's geometry is untouchable — refusing here
  // fails every node/edge/bulge/offset/straighten op soft in one place
  if (pm.get('pinned') === true) return null
  const v = pm.get(paramKey) as ParamValue | undefined
  if (!v || typeof v !== 'object' || !Array.isArray((v as Path2D).nodes)) return null
  return { pm, path: v as Path2D }
}

/**
 * Re-anchor every opening hosted in `elementId` so it keeps its WORLD position across
 * a path shape change: old arc-length offset → world anchor on the OLD path → nearest
 * point on the NEW path → new offset. Runs inside the caller's transaction (one undo
 * step). A wall shortened past an opening slides it to the nearest surviving point
 * (host-integrity "relocate", never orphan). Exported for topology.ts (cornerJoin,
 * setLocationLine) — split/join keep their own exact arc-length bookkeeping.
 */
export function rehostOpeningsForPathChange(
  model: ModelDoc,
  elementId: ElementId,
  oldPath: Path2D,
  newPath: Path2D,
): void {
  const openings = hostedElements(model, elementId)
  if (!openings.length) return
  // CURVES round: stations walk the FLATTENED path (identical to the bare node points
  // for bulge-free paths) — the same point list the solid generators and plan symbols
  // measure hostOffsetFt against, so a station means one thing everywhere.
  const oldPts = flattenPathPts(oldPath)
  const newPts = flattenPathPts(newPath)
  if (oldPts.length < 2 || newPts.length < 2) return
  const { elements } = maps(model)
  for (const op of openings) {
    const s = typeof op.params.hostOffsetFt === 'number' ? op.params.hostOffsetFt : 0
    const anchor = ops.polylinePointAt(oldPts, s).pt
    const sNew = ops.projectToPolyline(newPts, anchor).s
    if (Math.abs(sNew - s) < 1e-9) continue
    const oem = elements.get(op.id)
    if (!oem) continue
    const opm = oem.get('params') as Y.Map<unknown>
    opm.set('hostOffsetFt', sNew)
  }
}

// ---------------------------------------------------------------------------
// WALL JOINS (Matt, 2026-09-21: "when a wall end is joined with another wall...
// the end is healed and the wall is automatically connected... if the wall they
// are attached to moves, too, that connection shouldn't be lost." And the
// correction that shaped the whole design: "we need to make sure the actual
// wall properties for length are changing, not just a visual infill.")
//
// A join is REAL stored data (`startJoin`/`endJoin` on the wall category schema
// — `{ targetId, targetEnd }` or null), maintained SYMMETRICALLY (if A's start
// points at B's end, B's end points back at A's start) and applied by actually
// rewriting the dependent wall's own path NODE coordinates — never a derived
// render-time fixup. v1 scope: corner (end-to-end) joins only, one partner per
// end — a mid-span T-join is a known, documented gap (see cornerJoin/topology
// notes). Propagation runs as a DRY RUN first (`planWallJoinPropagation`) so a
// move that would collapse a dependent wall refuses the ENTIRE edit before
// anything is written — matching this file's existing fail-soft doctrine
// (moveNode et al. already "fail soft... when the ref no longer exists") rather
// than partially applying then trying to roll back a Yjs transaction.
// ---------------------------------------------------------------------------

export interface WallJoinRef {
  targetId: ElementId
  targetEnd: 'start' | 'end'
}

/** Below this, a wall is considered to have effectively disappeared. */
const MIN_WALL_LEN_FT = 0.1
/** Auto-detect/auto-break coincidence tolerance — matches CORNER_TOL in
 *  cornerWeld.ts (the render-time healing tolerance), so "looks joined" and
 *  "is joined" line up. */
const JOIN_COINCIDENCE_TOL_FT = 0.05

function wallJoinField(end: 'start' | 'end'): 'startJoin' | 'endJoin' {
  return end === 'start' ? 'startJoin' : 'endJoin'
}
function wallJoinDisabledField(end: 'start' | 'end'): 'startJoinDisabled' | 'endJoinDisabled' {
  return end === 'start' ? 'startJoinDisabled' : 'endJoinDisabled'
}

/**
 * JOINS FOLLOW FROM BOTH SIDES (2026-09-29, Matt: "when wall corners are associated
 * [why] don't they attempt to stay trimmed together when one moves?"). A live model had
 * 27 of 111 join refs broken — mostly ONE-SIDED (A points at B, B points elsewhere),
 * because one end holds one ref and a third wall arriving at a corner overwrote the
 * partner's. So a partner is now every wall END that references this end OR that this
 * end references (`reverseJoinIndex`), and a partner follows only when it is ACTUALLY
 * TOUCHING this end right now (within `JOIN_FOLLOW_TOL_FT`) and on the same level — a
 * stale ref whose ends drifted apart stays put (and keeps its "out of date" warning)
 * instead of being yanked across the gap, and a wall on another story never moves.
 *
 * Dry-run over the reachable join graph given `elementId`'s proposed NEW path. Each
 * following partner's joined end slides to the new endpoint (the rest of its path is
 * untouched), recursing into its own partners; refuses (false) the instant a wall would
 * collapse under `MIN_WALL_LEN_FT` or a touching partner is pinned. `visited` guards
 * re-walks (a closed room terminates); a partner's updates accumulate in `updates`, so
 * a wall touched at both ends moves both.
 */
const JOIN_FOLLOW_TOL_FT = 0.1

type JoinEnd = { id: ElementId; end: 'start' | 'end' }
function reverseJoinIndex(model: ModelDoc): Map<string, JoinEnd[]> {
  const idx = new Map<string, JoinEnd[]>()
  maps(model).elements.forEach((em, id) => {
    if (em.get('category') !== 'wall') return
    const pm = em.get('params') as Y.Map<unknown> | undefined
    if (!pm) return
    for (const end of ['start', 'end'] as const) {
      const j = pm.get(wallJoinField(end)) as WallJoinRef | null | undefined
      if (!j || typeof j !== 'object' || !j.targetId) continue
      const k = j.targetId + ':' + j.targetEnd
      const list = idx.get(k) ?? []
      list.push({ id: id as ElementId, end })
      idx.set(k, list)
    }
  })
  return idx
}

const pathEndPt = (p: Path2D, end: 'start' | 'end'): Pt2 => {
  const pts = flattenPathPts(p)
  return end === 'start' ? pts[0] : pts[pts.length - 1]
}

/**
 * JOINS NEVER ROTATE A PARTNER (Matt, 2026-10-08: "I won't want to accidentally cause
 * other walls to become off-axis because of a sticky wall join"). ONE follow engine for
 * every wall edit — it replaced two rules that each still swung partners: the node-drag
 * rule dragged a partner's END to the moved end (rotating it about its far end), and the
 * REVIT JOINS translate rule fell back to that same drag for a COLLINEAR partner.
 *
 * A partner is any wall END that references the moved end or that it references
 * (`reverseJoinIndex`), on the same level and ACTUALLY touching right now
 * (`JOIN_FOLLOW_TOL_FT`) — a stale ref stays put. For each one:
 *   - END DRAG (only this wall's end node moved — a grip drag, a typed length): the
 *     partner SHIFTS SQUARE to its own line by the part of the move across it, and its
 *     end slides along itself to the new corner — the corner lands exactly where it was
 *     put and the partner keeps its angle. Its far end then follows by the slide rule.
 *   - SEGMENT MOVE (the end segment moved as a whole — move, align, a dimension edit,
 *     location line, rotate/mirror): the partner's end SLIDES ALONG ITS OWN LINE to where
 *     it meets the moved wall's new line (extends or shortens, never turns), and with
 *     `retrimOwn` the moved wall's own end trims/extends to that same point so the corner
 *     holds. A COLLINEAR partner has no crossing: it slides when the new end is still on
 *     its line, else it shifts square with the move like an end drag.
 *   - a curved end segment keeps the old end-to-end follow (a curve has no single line).
 * Partners in `moving` (moving in the same gesture) are left alone. Refuses (null) when
 * a touching partner is pinned or a wall would collapse under `MIN_WALL_LEN_FT`.
 */
interface FollowCtx {
  model: ModelDoc
  rev: Map<string, JoinEnd[]>
  updates: Map<ElementId, Path2D>
  visited: Set<ElementId>
  moving: Set<ElementId>
  /** ALIGN TO GRID (2026-10-08, Matt: "don't allow it to kill any other alignments because
   *  of a join condition"): a partner may only SLIDE along its own line — one that could
   *  only follow by shifting sideways (or a curve) stays exactly where it is */
  strict?: boolean
  /** GROUP MOVE round 3 (2026-10-19, Matt: two walls in Grabowski "didn't work"): a MOVE
   *  that would crush a joined wall to nothing (or flip it past its far end) disjoins from
   *  it instead — Revit's Disjoin — so the moved wall goes and the short wall stays put,
   *  unjoined. Collected here; translateElement clears the join refs. Absent = refuse. */
  disjoin?: { a: JoinEnd; b: JoinEnd }[]
}

/** a partner end would collapse: shorter than the minimum, or pushed past its far end */
function collapsesAt(far: Pt2, end0: Pt2, X: Pt2): boolean {
  if (ptDist(far, X) < MIN_WALL_LEN_FT) return true
  return (X[0] - far[0]) * (end0[0] - far[0]) + (X[1] - far[1]) * (end0[1] - far[1]) <= 0
}

const SIN_PARALLEL = Math.sin((3 * Math.PI) / 180)
const ptDist = (a: Pt2, b: Pt2) => Math.hypot(a[0] - b[0], a[1] - b[1])

function endPartners(ctx: FollowCtx, pm: Y.Map<unknown>, elementId: ElementId, end: 'start' | 'end'): JoinEnd[] {
  const out: JoinEnd[] = []
  const fwd = pm.get(wallJoinField(end)) as WallJoinRef | null | undefined
  if (fwd && typeof fwd === 'object' && fwd.targetId) out.push({ id: fwd.targetId, end: fwd.targetEnd })
  for (const r of ctx.rev.get(elementId + ':' + end) ?? []) {
    if (!out.some((p) => p.id === r.id && p.end === r.end)) out.push(r)
  }
  return out.filter((p) => p.id !== elementId)
}

/** where line (a, a+d) meets line (b, b+e); null when (nearly) parallel or absurdly far */
function lineCross(a: Pt2, d: Pt2, b: Pt2, e: Pt2, near: Pt2): Pt2 | null {
  const l1 = Math.hypot(d[0], d[1]), l2 = Math.hypot(e[0], e[1])
  const cr = d[0] * e[1] - d[1] * e[0]
  if (l1 < 1e-9 || l2 < 1e-9 || Math.abs(cr) / (l1 * l2) <= SIN_PARALLEL) return null
  const t = ((b[0] - a[0]) * e[1] - (b[1] - a[1]) * e[0]) / cr
  const X: Pt2 = [a[0] + d[0] * t, a[1] + d[1] * t]
  return ptDist(X, near) < 500 ? X : null
}

/** shift a partner square by `shift`, put its joined end at `endPt`, and let its far end
 *  follow (slide rule) — false to refuse */
function shiftPartner(ctx: FollowCtx, p: JoinEnd, depPath: Path2D, shift: Pt2, endPt: Pt2, from?: JoinEnd): boolean | 'disjoin' {
  const moved = ops.clonePath(depPath)
  for (const nd of moved.nodes) nd.pt = [nd.pt[0] + shift[0], nd.pt[1] + shift[1]]
  const k = p.end === 'start' ? 0 : moved.nodes.length - 1
  moved.nodes[k] = { ...moved.nodes[k], pt: [endPt[0], endPt[1]] }
  const far = moved.nodes[p.end === 'start' ? moved.nodes.length - 1 : 0].pt
  if (ptDist(far, endPt) < MIN_WALL_LEN_FT) {
    if (ctx.disjoin && from) { ctx.disjoin.push({ a: from, b: p }); return 'disjoin' }
    return false
  }
  ctx.updates.set(p.id, moved)
  if (ctx.visited.has(p.id)) return true
  const res = followJoins(ctx, p.id, depPath, moved, true)
  if (!res) return false
  ctx.updates.set(p.id, res)
  return true
}

/** slide a partner's joined end along its own line to `X` — false to refuse */
function slidePartnerEnd(ctx: FollowCtx, p: JoinEnd, depPath: Path2D, X: Pt2, from?: JoinEnd): boolean | 'disjoin' {
  const nodes = ops.clonePath(depPath).nodes
  const k = p.end === 'start' ? 0 : nodes.length - 1
  const far = nodes[p.end === 'start' ? nodes.length - 1 : 0].pt
  // GROUP MOVE round 3: pushing the end PAST the far end used to pass (only the distance
  // was checked) and flipped the wall inside out — it collapses like a too-short one
  if (collapsesAt(far, nodes[k].pt, X)) {
    if (ctx.disjoin && from) { ctx.disjoin.push({ a: from, b: p }); return 'disjoin' }
    return false
  }
  nodes[k] = { ...nodes[k], pt: [X[0], X[1]] }
  const next: Path2D = { closed: depPath.closed, nodes }
  ctx.updates.set(p.id, next)
  if (ctx.visited.has(p.id)) return true
  // walls joined to THIS partner's moved end (a three-wall corner) follow it in turn
  return followJoins(ctx, p.id, depPath, next, false) !== null
}

function followJoins(ctx: FollowCtx, elementId: ElementId, oldPath: Path2D, newPath: Path2D, retrimOwn: boolean): Path2D | null {
  ctx.visited.add(elementId)
  const em = maps(ctx.model).elements.get(elementId)
  if (!em || em.get('category') !== 'wall') return newPath
  const pm = em.get('params') as Y.Map<unknown> | undefined
  if (!pm) return newPath
  const oldPts = flattenPathPts(oldPath), newPts = flattenPathPts(newPath)
  if (oldPts.length < 2 || newPts.length < 2) return newPath
  const levelId = em.get('levelId')
  let own: Path2D | null = null
  for (const myEnd of ['start', 'end'] as const) {
    const last = (arr: Pt2[]) => arr[arr.length - 1]
    const oldPt = myEnd === 'start' ? oldPts[0] : last(oldPts)
    const newPt = myEnd === 'start' ? newPts[0] : last(newPts)
    if (ptDist(newPt, oldPt) < 1e-9) continue   // this end didn't move
    const oldNbr = myEnd === 'start' ? oldPts[1] : oldPts[oldPts.length - 2]
    const newNbr = myEnd === 'start' ? newPts[1] : newPts[newPts.length - 2]
    const endDrag = oldPts.length === newPts.length && ptDist(oldNbr, newNbr) < 1e-9
    const myEdgeFrom = myEnd === 'start' ? 0 : newPath.nodes.length - 2
    const myStraight = !newPath.nodes[myEdgeFrom]?.bulge
    const partners = endPartners(ctx, pm, elementId, myEnd)
    for (const p of partners) {
      // a wall this follow already resolved (the moved wall itself, or one reached around a
      // closed room) is never re-moved from its stale stored path
      if (ctx.moving.has(p.id) || ctx.visited.has(p.id)) continue
      const dep = getElement(ctx.model, p.id)
      if (!dep || dep.category !== 'wall' || dep.levelId !== levelId) continue
      const depPath = ctx.updates.get(p.id) ?? (dep.params.path as Path2D | null)
      if (!depPath || !Array.isArray(depPath.nodes) || depPath.nodes.length < 2) continue
      const depPt = pathEndPt(depPath, p.end)
      if (ptDist(depPt, oldPt) > JOIN_FOLLOW_TOL_FT) continue   // stale ref — stays put
      if (dep.params.pinned === true) return null
      const n = depPath.nodes.length
      const depIdx = p.end === 'start' ? 0 : n - 1
      const nbrIdx = p.end === 'start' ? 1 : n - 2
      const depStraight = !depPath.nodes[Math.min(depIdx, nbrIdx)].bulge
      if (!depStraight) {
        if (ctx.strict) continue   // strict: a curve can't slide — it stays put
        if (!slidePartnerEnd(ctx, p, depPath, newPt, { id: elementId, end: myEnd })) return null   // a curve: end-to-end
        continue
      }
      const depNbr = depPath.nodes[nbrIdx].pt
      const d: Pt2 = [depPt[0] - depNbr[0], depPt[1] - depNbr[1]]
      const dl = Math.hypot(d[0], d[1]) || 1
      const du: Pt2 = [d[0] / dl, d[1] / dl]
      // every partner at this corner is being resolved here: none may be shifted again
      // by another partner's follow
      for (const q of partners) if (q.id !== p.id) ctx.moving.add(q.id)
      const v: Pt2 = [newPt[0] - oldPt[0], newPt[1] - oldPt[1]]
      const along = v[0] * du[0] + v[1] * du[1]
      const across: Pt2 = [v[0] - du[0] * along, v[1] - du[1] * along]
      // the move runs along the partner's own line — it just extends/shortens
      const onLine = Math.hypot(across[0], across[1]) < 1e-9
      let X: Pt2 | null = null
      if (!endDrag && !onLine && myStraight) {
        X = lineCross(depPt, d, newPt, [newPt[0] - newNbr[0], newPt[1] - newNbr[1]], newPt)
      }
      const from: JoinEnd = { id: elementId, end: myEnd }
      let ok: boolean | 'disjoin'
      if (onLine) ok = slidePartnerEnd(ctx, p, depPath, newPt, from)
      else if (X) {
        ok = slidePartnerEnd(ctx, p, depPath, X, from)
        // a disjoined partner stays behind: this wall keeps its own moved end
        if (ok === true && retrimOwn && ptDist(X, newPt) > 1e-9) {
          if (!own) own = ops.clonePath(newPath)
          const k = myEnd === 'start' ? 0 : own.nodes.length - 1
          own.nodes[k] = { ...own.nodes[k], pt: X }
          const otherEnd = own.nodes[myEnd === 'start' ? own.nodes.length - 1 : 0].pt
          if (ptDist(otherEnd, X) < MIN_WALL_LEN_FT) return null
        }
      } else if (ctx.strict) ok = true   // strict: never shift another wall sideways
      else ok = shiftPartner(ctx, p, depPath, across, newPt, from)   // end drag / collinear
      if (!ok) return null
    }
  }
  return own ?? newPath
}

/** Refuses (null) if propagating `newPath` would collapse or move a pinned joined wall;
 *  otherwise returns the set of OTHER walls that must move (never including `elementId`
 *  itself — the caller writes that one's own path separately). */
export function planWallJoinPropagation(model: ModelDoc, elementId: ElementId, newPath: Path2D): Map<ElementId, Path2D> | null {
  const cur = getElement(model, elementId)
  const oldPath = (cur?.params.path as Path2D | null) ?? newPath
  const ctx: FollowCtx = { model, rev: reverseJoinIndex(model), updates: new Map(), visited: new Set([elementId]), moving: new Set() }
  return followJoins(ctx, elementId, oldPath, newPath, false) ? ctx.updates : null
}

/**
 * JOIN CLEANUP (2026-09-29, Matt: "Build and do the cleanup"): repairs every wall join ref
 * in a model to the one consistent state the follow logic expects —
 *   • a ref to a deleted/non-wall target, a wall on ANOTHER level, or an end that no
 *     longer touches (> JOIN_FOLLOW_TOL_FT) is cleared;
 *   • a valid touching ref whose partner end is EMPTY (or stale) gets the back-ref, so
 *     the pair is symmetric again (a partner end already holding a different valid ref
 *     keeps it — three walls at one corner still follow, via the reverse lookup);
 *   • two free wall ends on the same level that touch — and are the ONLY two wall ends
 *     at that point, neither with "Disallow Join" — are linked.
 * One transaction (one undo step). Returns counts for the caller's report.
 */
export function repairWallJoins(model: ModelDoc): { cleared: number; mirrored: number; linked: number } {
  const { elements } = maps(model)
  type WallEnd = { id: ElementId; end: 'start' | 'end'; pt: Pt2; levelId: unknown; pm: Y.Map<unknown> }
  const ends: WallEnd[] = []
  const byId = new Map<string, { levelId: unknown; pm: Y.Map<unknown>; path: Path2D }>()
  elements.forEach((em, id) => {
    if (em.get('category') !== 'wall') return
    const pm = em.get('params') as Y.Map<unknown> | undefined
    const path = pm?.get('path') as Path2D | undefined
    if (!pm || !path || !Array.isArray(path.nodes) || path.nodes.length < 2) return
    byId.set(id, { levelId: em.get('levelId'), pm, path })
    for (const end of ['start', 'end'] as const) ends.push({ id: id as ElementId, end, pt: pathEndPt(path, end), levelId: em.get('levelId'), pm })
  })
  const endOf = (id: string, end: 'start' | 'end') => ends.find((e) => e.id === id && e.end === end)
  const touching = (a: Pt2, b: Pt2) => Math.hypot(a[0] - b[0], a[1] - b[1]) <= JOIN_FOLLOW_TOL_FT
  const valid = (e: WallEnd, j: WallJoinRef | null | undefined) => {
    if (!j || typeof j !== 'object' || !j.targetId || j.targetId === e.id) return false
    const t = endOf(j.targetId, j.targetEnd)
    return !!t && t.levelId === e.levelId && touching(t.pt, e.pt)
  }
  let cleared = 0, mirrored = 0, linked = 0
  transact(model, () => {
    // 1. clear what can't be right
    for (const e of ends) {
      const j = e.pm.get(wallJoinField(e.end)) as WallJoinRef | null | undefined
      if (j && !valid(e, j)) { e.pm.set(wallJoinField(e.end), null); cleared++ }
    }
    // 2. mirror valid refs onto empty partner ends
    for (const e of ends) {
      const j = e.pm.get(wallJoinField(e.end)) as WallJoinRef | null | undefined
      if (!j) continue
      const t = endOf(j.targetId, j.targetEnd)!
      const back = t.pm.get(wallJoinField(t.end)) as WallJoinRef | null | undefined
      if (!back) { t.pm.set(wallJoinField(t.end), { targetId: e.id, targetEnd: e.end } satisfies WallJoinRef); mirrored++ }
    }
    // 3. link two-and-only-two touching free ends
    for (let i = 0; i < ends.length; i++) {
      const a = ends[i]
      if (a.pm.get(wallJoinField(a.end)) || a.pm.get(wallJoinDisabledField(a.end)) === true) continue
      const here = ends.filter((o) => o.levelId === a.levelId && touching(o.pt, a.pt))
      if (here.length !== 2) continue
      const b = here.find((o) => !(o.id === a.id && o.end === a.end))!
      if (b.id === a.id) continue
      if (b.pm.get(wallJoinField(b.end)) || b.pm.get(wallJoinDisabledField(b.end)) === true) continue
      a.pm.set(wallJoinField(a.end), { targetId: b.id, targetEnd: b.end } satisfies WallJoinRef)
      b.pm.set(wallJoinField(b.end), { targetId: a.id, targetEnd: a.end } satisfies WallJoinRef)
      linked++
    }
  })
  return { cleared, mirrored, linked }
}

/** Applies a plan from `planWallJoinPropagation` — call ONLY inside the same transaction
 *  as the triggering write, after that write, so it's all one undo step. */
export function applyJoinPropagation(model: ModelDoc, updates: Map<ElementId, Path2D>): void {
  const { elements } = maps(model)
  for (const [id, path] of updates) {
    const em = elements.get(id)
    if (!em) continue
    const pm = em.get('params') as Y.Map<unknown>
    const oldPath = pm.get('path') as Path2D | undefined
    pm.set('path', path)
    if (oldPath) rehostOpeningsForPathChange(model, id, oldPath, path)
  }
}

/** Breaks a join on one end, symmetrically — clears it on both this wall and (if the
 *  back-reference still points here) the partner. Safe to call on an already-clear end. */
export function clearWallJoin(model: ModelDoc, elementId: ElementId, end: 'start' | 'end'): void {
  const em = maps(model).elements.get(elementId)
  if (!em) return
  const pm = em.get('params') as Y.Map<unknown>
  const field = wallJoinField(end)
  const join = pm.get(field) as WallJoinRef | null | undefined
  if (!join) return
  pm.set(field, null)
  const oem = maps(model).elements.get(join.targetId)
  if (!oem) return
  const opm = oem.get('params') as Y.Map<unknown>
  const backField = wallJoinField(join.targetEnd)
  const back = opm.get(backField) as WallJoinRef | null | undefined
  if (back && back.targetId === elementId) opm.set(backField, null)
}

/**
 * AUTO-DETECT / AUTO-BREAK: after a wall END node settles at `nodeId`'s new position
 * (a node-drag release — `moveNode` is the one chokepoint every such gesture commits
 * through), checks whether that end now coincides with another wall's end within
 * `JOIN_COINCIDENCE_TOL_FT`. If so — and neither end has "Disallow Join" set — creates a
 * real, symmetric join. If the end used to be joined and no longer coincides with its old
 * partner, the join breaks (dragging an end away IS the "user did something that makes it
 * impossible to maintain" case). No-ops on an interior node (not an end at all).
 *
 * v1 scope: wired into `moveNode`'s wall path and `cornerJoin`'s own node writes — the
 * gestures that actually bring two wall ends into coincidence today. Drawing a brand-new
 * wall snapped onto an existing corner, or a wall arriving there via split/offset, does
 * not yet auto-detect (a known, documented gap, not silent breakage — those cases just
 * behave as before, with no persistent join, until a future round extends detection to
 * them using this same helper).
 */
export function autoDetectWallJoinAtNode(model: ModelDoc, elementId: ElementId, nodeId: NodeId): void {
  const em = maps(model).elements.get(elementId)
  if (!em || em.get('category') !== 'wall') return
  const pm = em.get('params') as Y.Map<unknown> | undefined
  const path = pm?.get('path') as Path2D | undefined
  if (!pm || !path || !Array.isArray(path.nodes) || path.nodes.length < 2) return
  const idx = path.nodes.findIndex((n) => n.id === nodeId)
  if (idx !== 0 && idx !== path.nodes.length - 1) return
  const myEnd: 'start' | 'end' = idx === 0 ? 'start' : 'end'
  const myPt = path.nodes[idx].pt
  const field = wallJoinField(myEnd)
  const oldJoin = pm.get(field) as WallJoinRef | null | undefined

  if (pm.get(wallJoinDisabledField(myEnd)) === true) {
    if (oldJoin) clearWallJoin(model, elementId, myEnd)
    return
  }

  if (oldJoin) {
    const dep = getElement(model, oldJoin.targetId)
    const depPath = dep?.params.path as Path2D | null
    if (dep && depPath?.nodes?.length) {
      const depPt = oldJoin.targetEnd === 'start' ? depPath.nodes[0].pt : depPath.nodes[depPath.nodes.length - 1].pt
      if (Math.hypot(depPt[0] - myPt[0], depPt[1] - myPt[1]) < JOIN_COINCIDENCE_TOL_FT) return   // still coincident
    }
    clearWallJoin(model, elementId, myEnd)
  }

  const { elements } = maps(model)
  let found: { id: ElementId; end: 'start' | 'end' } | null = null
  const myLevel = em.get('levelId')
  elements.forEach((otherEm, otherId) => {
    // JOIN CLEANUP round: a join is between walls on the SAME level — a wall on the story
    // above that happens to share the corner in plan must never be dragged along
    if (found || otherId === elementId || otherEm.get('category') !== 'wall' || otherEm.get('levelId') !== myLevel) return
    const opm = otherEm.get('params') as Y.Map<unknown> | undefined
    const op = opm?.get('path') as Path2D | undefined
    if (!opm || !op || !Array.isArray(op.nodes) || op.nodes.length < 2) return
    for (const end of ['start', 'end'] as const) {
      if (opm.get(wallJoinDisabledField(end)) === true) continue
      const pt = end === 'start' ? op.nodes[0].pt : op.nodes[op.nodes.length - 1].pt
      if (Math.hypot(pt[0] - myPt[0], pt[1] - myPt[1]) < JOIN_COINCIDENCE_TOL_FT) {
        found = { id: otherId as ElementId, end }
        break
      }
    }
  })
  if (found) {
    const f = found as { id: ElementId; end: 'start' | 'end' }
    pm.set(field, { targetId: f.id, targetEnd: f.end } satisfies WallJoinRef)
    const oem = elements.get(f.id)
    // the back-ref goes on only when the partner end is free — overwriting a third wall's
    // join there is exactly how refs became one-sided (the reverse lookup in
    // computeJoinPropagation keeps a one-sided ref following anyway)
    const opm = oem?.get('params') as Y.Map<unknown> | undefined
    if (opm && !opm.get(wallJoinField(f.end))) opm.set(wallJoinField(f.end), { targetId: elementId, targetEnd: myEnd } satisfies WallJoinRef)
  }
}

function writePath(
  model: ModelDoc, elementId: ElementId, pm: Y.Map<unknown>, paramKey: string,
  oldPath: Path2D, path: Path2D, postWrite?: () => void, preWrite?: () => void, followJoins = true,
): boolean {
  // P6-clean: re-anchor hosted elements whenever the edited param IS this category's
  // declared plan geometry (registry pathParamKey) — the old `paramKey === 'path'`
  // hardcode silently skipped boundary-based hosts (the skylight-into-roof blocker)
  const em = maps(model).elements.get(elementId)
  const cat = em?.get('category') as CategoryId | undefined
  const hostsHere = cat != null && pathParamKeyFor(cat) === paramKey
  // STAIR round (audit item 2): a stair's run length is DERIVED from the riser
  // count, never free-drawn — every grip edit re-normalizes the path here (the one
  // choke point all node/edge ops flow through), so a drag sets DIRECTION and the
  // length snaps to the true run on commit. Ghost tails can't survive an edit.
  if (cat === 'stair' && paramKey === 'path') {
    const r = resolveElement(model, elementId)
    const extent = r && stairVerticalExtent(r)
    if (r && extent) {
      const { maxRiserFt, treadDepthFt } = stairStepInputs(r.params, extent.zTop - extent.zBase)
      const sOpts = {
        widthFt: (r.params.widthFt as number) || 3.5,
        floorToFloorFt: Math.max(extent.zTop - extent.zBase, maxRiserFt),
        maxRiserFt,
        treadDepthFt,
      }
      const norm = normalizeStairRunPath(path, sOpts, r.params)
      if (norm) path = norm
      // RUN-BASED STAIRS (2026-10-02, Matt: "never allowed to blow up the stair's geometry
      // with a move that wouldn't be able to be logically derived"): refuse an edit that
      // leaves a run with no treads, or a stair that no longer lays out at all
      if (r.params.runLayout === 'runs' && r.params.stairShape !== 'spiral' && r.params.stairShape !== 'helical') {
        const lay = stairLayoutForPath(path, sOpts, r.params)
        if (!lay) return false
        const runSegs = path.nodes.slice(0, -1).map((nd, i) => i).filter((i) => !path.nodes[i].edge?.landing)
        if (runSegs.some((i) => !lay.legs.some((lg) => lg.seg === i))) return false
      }
    }
  }
  const isWall = cat === 'wall' && paramKey === 'path'
  const joinPlan = isWall && followJoins ? planWallJoinPropagation(model, elementId, path) : new Map<ElementId, Path2D>()
  if (isWall && joinPlan === null) return false   // would collapse a joined wall — refuse, mutating nothing
  transact(model, () => {
    preWrite?.()
    pm.set(paramKey, path)
    if (hostsHere) rehostOpeningsForPathChange(model, elementId, oldPath, path)
    if (isWall) applyJoinPropagation(model, joinPlan!)
    postWrite?.()
  })
  return true
}

/**
 * `opts.followJoins` (default true): a joined partner's end follows the moved end — the
 * grip-drag behavior that keeps a corner a corner. TRIM/EXTEND (2026-10-02, Matt: "allow
 * when we use the trim/extend command that it won't torque the connected wall") passes
 * false: the wall end RELEASES its join first (both refs, plus any one-sided ref another
 * wall holds onto this end) and moves alone — the partner stays exactly where it is and
 * now butts the adjusted wall as a T. A new coincidence at the end's destination still
 * auto-joins, as on any move.
 */
export function moveNode(
  model: ModelDoc, elementId: ElementId, paramKey: string, ref: NodeId, pt: Pt2,
  opts: { followJoins?: boolean } = {},
): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  const next = ops.moveNode(r.path, ref, pt)
  if (!next) return false
  const follow = opts.followJoins !== false
  const idx = r.path.nodes.findIndex((n) => n.id === ref)
  const end: 'start' | 'end' | null = idx === 0 ? 'start' : idx === r.path.nodes.length - 1 ? 'end' : null
  const release = follow || !end ? undefined : () => {
    clearWallJoin(model, elementId, end)
    maps(model).elements.forEach((oem) => {
      if (oem.get('category') !== 'wall') return
      const opm = oem.get('params') as Y.Map<unknown> | undefined
      if (!opm) return
      for (const f of [wallJoinField('start'), wallJoinField('end')]) {
        const j = opm.get(f) as WallJoinRef | null | undefined
        if (j && j.targetId === elementId && j.targetEnd === end) opm.set(f, null)
      }
    })
  }
  return writePath(model, elementId, r.pm, paramKey, r.path, next, () => {
    autoDetectWallJoinAtNode(model, elementId, ref)
  }, release, follow)
}

/**
 * OFF-AXIS AUDIT round (Matt, 2026-08-18: "add a button near the warning to
 * straighten the wall"): rotates ONE edge's TO node around its FROM node by the
 * exact signed angle needed to land the edge on the nearest cardinal direction
 * (0/90/180/270°, per `offAxis.ts#edgeAngleInfo`) — a PURE rotation, so the edge's
 * own length never changes, only its direction. The FROM node (`fromNodeId`) never
 * moves: any corner/join anchored there survives untouched; only the far end of
 * THIS edge shifts — by a sub-inch amount at typical wall lengths, since the round
 * only ever warns within 1° of square. Re-anchors hosted openings for free (routes
 * through `moveNode`'s own `writePath`, same as any other node move).
 *
 * Scoped to ONE edge deliberately, not "straighten the whole wall": a
 * multi-segment (joined) wall's OTHER edges are independent and may not need it,
 * and a wall sharing this exact corner with a SEPARATE (unjoined) wall element
 * isn't adjusted here — this fixes what's actually named, never a wider blast
 * radius than the button the user clicked.
 */
export function straightenEdge(model: ModelDoc, elementId: ElementId, paramKey: string, fromNodeId: NodeId): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  const i = r.path.nodes.findIndex((n) => n.id === fromNodeId)
  if (i < 0) return false
  if (!r.path.closed && i === r.path.nodes.length - 1) return false
  const j = (i + 1) % r.path.nodes.length
  const a = r.path.nodes[i].pt, b = r.path.nodes[j].pt
  const info = edgeAngleInfo(a, b, fromNodeId, r.path.nodes[i].bulge)
  if (!info) return false
  // shortest signed rotation to the nearest cardinal — never the long way around
  const deltaDeg = ((info.nearestOrthoDeg - info.angleDeg + 540) % 360) - 180
  const rad = (deltaDeg * Math.PI) / 180
  const c = Math.cos(rad), s = Math.sin(rad)
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const newB: Pt2 = [a[0] + dx * c - dy * s, a[1] + dx * s + dy * c]
  return moveNode(model, elementId, paramKey, r.path.nodes[j].id, newB)
}

export function insertNode(model: ModelDoc, elementId: ElementId, paramKey: string, edge: EdgeRef, pt: Pt2): NodeId | null {
  const r = readPath(model, elementId, paramKey)
  if (!r) return null
  const next = ops.insertNode(r.path, edge, pt)
  if (!next) return null
  if (!writePath(model, elementId, r.pm, paramKey, r.path, next.path)) return null
  return next.nodeId
}

export function deleteNode(model: ModelDoc, elementId: ElementId, paramKey: string, ref: NodeId): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  const next = ops.deleteNode(r.path, ref)
  if (!next) return false
  return writePath(model, elementId, r.pm, paramKey, r.path, next)
}

export function moveEdge(model: ModelDoc, elementId: ElementId, paramKey: string, edge: EdgeRef, delta: Pt2): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  const next = ops.moveEdge(r.path, edge, delta)
  if (!next) return false
  return writePath(model, elementId, r.pm, paramKey, r.path, next)
}

/** CURVES round: set/clear one edge's bulge (the midpoint bulge grip's commit).
 *  Hosted openings re-anchor through the shared writePath path like any shape edit. */
export function setEdgeBulge(model: ModelDoc, elementId: ElementId, paramKey: string, edge: EdgeRef, bulge: number): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  const next = ops.setBulge(r.path, edge, bulge)
  if (!next) return false
  return writePath(model, elementId, r.pm, paramKey, r.path, next)
}

export function offsetEdge(model: ModelDoc, elementId: ElementId, paramKey: string, edge: EdgeRef, distanceFt: number): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  const next = ops.offsetEdge(r.path, edge, distanceFt)
  if (!next) return false
  return writePath(model, elementId, r.pm, paramKey, r.path, next)
}

export function offsetAllEdges(model: ModelDoc, elementId: ElementId, paramKey: string, distanceFt: number): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  const next = ops.offsetAllEdges(r.path, distanceFt)
  if (!next) return false
  return writePath(model, elementId, r.pm, paramKey, r.path, next)
}

/**
 * Rigidly translate an element's whole path/boundary by `delta` (Matt r3 — the pet
 * palette's Move). Hosted openings ride along automatically: `hostOffsetFt` is arc
 * length from the path start, which a rigid translate preserves, so NO re-hosting runs
 * (unlike a shape edit). One transaction = one undo step. For a wall, both ends move at
 * once — WALL JOINS propagate through the same dry-run-then-apply plan as `writePath`
 * (refuses, mutating nothing, if the translate would collapse a joined wall); a
 * translate never auto-detects NEW joins (v1 scope — see `autoDetectWallJoinAtNode`).
 */
export function translateElement(
  model: ModelDoc, elementId: ElementId, delta: Pt2,
  /** the other elements moving in the SAME gesture — their joints travel with them,
   *  never dragged (REVIT JOINS round) */
  movingSet: Set<ElementId> | null = null,
  /** GROUP MOVE round 3: filled with what the move had to do or couldn't do, so the UI
   *  can say it (never a silent no-op) */
  report: MoveReport | null = null,
): boolean {
  const em = maps(model).elements.get(elementId)
  if (!em) return false
  // SKETCH GEOMETRY (2026-09-27): a body has no path — its geometry is params.topology.
  // It used to fall out here silently (and a Copy left an unmoved duplicate behind);
  // now the whole body translates through the body kernel (validity-gated).
  if (em.get('category') === 'body') {
    return moveBodyParts(model, elementId, null, (p) => ({ x: p.x + delta[0], y: p.y + delta[1], z: p.z })).ok
  }
  const pm = em.get('params') as Y.Map<unknown> | undefined
  if (!pm) return false
  if (pm.get('pinned') === true) { if (report) report.pinned++; return false }   // MODIFY PHASE-2 (Pin)
  const category = em.get('category') as CategoryId | undefined
  const key = geomKeyFor(pm, category)
  if (!key) return false
  const path = pm.get(key) as Path2D | undefined
  if (!path || !Array.isArray(path.nodes)) return false
  const moved = ops.clonePath(path)
  for (const n of moved.nodes) n.pt = [n.pt[0] + delta[0], n.pt[1] + delta[1]]
  const isWall = category === 'wall' && key === 'path'
  // REVIT JOINS round: a MOVED wall keeps its joins the way Revit does — see
  // planTranslateJoins (the node-drag follow rule stays planWallJoinPropagation)
  let plan = isWall ? planTranslateJoins(model, elementId, path, moved, movingSet) : null
  // GROUP MOVE round 3: a joined wall that would be crushed is DISJOINED, not a refusal
  if (isWall && plan === null) plan = planTranslateJoins(model, elementId, path, moved, movingSet, true)
  if (isWall && plan === null) { if (report) report.blocked++; return false }
  transact(model, () => {
    for (const dj of plan?.disjoin ?? []) clearJoinBetween(model, dj.a, dj.b)
    if (report && plan?.disjoin?.length) report.disjoined += plan.disjoin.length
    pm.set(key, plan ? plan.own : moved)
    moveRiderLoops(pm, key, (p) => {
      const q = ops.clonePath(p)
      for (const n of q.nodes) n.pt = [n.pt[0] + delta[0], n.pt[1] + delta[1]]
      return q
    })
    if (plan && plan.own !== moved) rehostOpeningsForPathChange(model, elementId, moved, plan.own)
    if (plan) applyJoinPropagation(model, plan.updates)
    // DINING CHAIRS BY COUNT: a table carries its chairs (unless they move in this gesture)
    if (category === 'object' && isDiningTable(model, elementId)) {
      for (const c of chairsOfTable(model, elementId)) {
        if (movingSet?.has(c)) continue
        translateElement(model, c, delta, movingSet, report)
      }
    }
  })
  return true
}

/**
 * STRETCH (2026-10-23, Matt: "a stretch style modify command … like stretch in AutoCAD but
 * for the model" — ArchiCAD has it, Revit doesn't): move only the path/boundary NODES that
 * `inside` says sit in the crossing window, by `delta`; every other node stays put, so a wall
 * crossing the window's edge lengthens or shortens and a floor/roof/ceiling boundary
 * reshapes. Floor/ceiling openings and roof cuts (the boundary's rider loops) stretch by the
 * same per-point rule. Doors/windows hosted here whose anchor sits inside the window travel
 * with the stretch; the rest keep their world position (the shared re-anchor).
 *
 * Joined walls are NOT followed (`followJoins` false): a corner whose point is in the window
 * moves in both walls of that corner by the same rule, so the joint stays a joint without
 * dragging a partner whose other end is outside the window. Fully-enclosed elements are the
 * caller's job (stretchElements translates them whole).
 */
export function stretchElementPath(
  model: ModelDoc, elementId: ElementId, inside: (p: Pt2) => boolean, delta: Pt2,
): 'stretched' | 'none' | 'pinned' | 'refused' {
  const em = maps(model).elements.get(elementId)
  if (!em) return 'none'
  const pm = em.get('params') as Y.Map<unknown> | undefined
  if (!pm) return 'none'
  const category = em.get('category') as CategoryId | undefined
  const key = geomKeyFor(pm, category)
  if (!key) return 'none'
  const path = pm.get(key) as Path2D | undefined
  if (!path || !Array.isArray(path.nodes) || !path.nodes.length) return 'none'
  const mv = (p: Pt2): Pt2 => (inside(p) ? [p[0] + delta[0], p[1] + delta[1]] : p)
  const next = ops.clonePath(path)
  let hit = false
  for (const n of next.nodes) if (inside(n.pt)) { n.pt = mv(n.pt); hit = true }
  let riderHit = false
  if (key === 'boundary') {
    for (const k of BOUNDARY_RIDER_KEYS) {
      const v = pm.get(k) as Path2D[] | null | undefined
      if (Array.isArray(v) && v.some((p) => p?.nodes?.some((n) => inside(n.pt)))) riderHit = true
    }
  }
  if (!hit && !riderHit) return 'none'
  if (pm.get('pinned') === true) return 'pinned'
  // a wall run squeezed to nothing is refused, never written
  if (category === 'wall' && key === 'path') {
    const p = flattenPathPts(next)
    let len = 0
    for (let i = 1; i < p.length; i++) len += Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1])
    if (len < MIN_WALL_LEN_FT) return 'refused'
  }
  // the hosted doors/windows whose anchor is inside the window ride the stretch
  const travel: { id: ElementId; to: Pt2 }[] = []
  if (pathParamKeyFor(category as CategoryId) === key) {
    const oldPts = flattenPathPts(path)
    if (oldPts.length >= 2) {
      for (const op of hostedElements(model, elementId)) {
        if ((op.params.path as Path2D | undefined)?.nodes?.length) continue   // own geometry: the caller's
        const s = typeof op.params.hostOffsetFt === 'number' ? op.params.hostOffsetFt : 0
        const a = ops.polylinePointAt(oldPts, s).pt
        if (inside(a)) travel.push({ id: op.id, to: mv(a) })
      }
    }
  }
  const ok = writePath(model, elementId, pm, key, path, next, () => {
    moveRiderLoops(pm, key, (p) => {
      const q = ops.clonePath(p)
      for (const n of q.nodes) n.pt = mv(n.pt)
      return q
    })
    if (!travel.length) return
    // the path as WRITTEN (a stair re-normalizes inside writePath)
    const newPts = flattenPathPts(pm.get(key) as Path2D)
    if (newPts.length < 2) return
    const { elements } = maps(model)
    for (const t of travel) {
      const opm = elements.get(t.id)?.get('params') as Y.Map<unknown> | undefined
      opm?.set('hostOffsetFt', ops.projectToPolyline(newPts, t.to).s)
    }
  }, undefined, false)
  return ok ? 'stretched' : 'refused'
}

/**
 * ALIGN TO GRID (Matt, 2026-10-08): move ONE run of a wall (`fromNodeId` = the run's
 * from-node; null = the whole wall) by `delta`, in STRICT follow mode — walls joined to it
 * extend/shorten along their own lines and nothing else ever shifts or turns, so a fix
 * here can't knock a neighbour off its own grid. One undo step; false when pinned or a
 * joined wall would collapse.
 */
export function alignWallRun(model: ModelDoc, elementId: ElementId, fromNodeId: NodeId | null, delta: Pt2): boolean {
  const r = readPath(model, elementId, 'path')
  if (!r) return false
  const em = maps(model).elements.get(elementId)
  if (em?.get('category') !== 'wall') return false
  let next: Path2D | null
  if (!fromNodeId || r.path.nodes.length <= 2) {
    next = ops.clonePath(r.path)
    for (const n of next.nodes) n.pt = [n.pt[0] + delta[0], n.pt[1] + delta[1]]
  } else {
    next = ops.moveEdge(r.path, { fromNodeId }, delta)
  }
  if (!next) return false
  const ctx: FollowCtx = {
    model, rev: reverseJoinIndex(model), updates: new Map(), visited: new Set([elementId]),
    moving: new Set(), strict: true,
  }
  const own = followJoins(ctx, elementId, r.path, next, true)
  if (!own) return false
  transact(model, () => {
    r.pm.set('path', own)
    rehostOpeningsForPathChange(model, elementId, r.path, own)
    applyJoinPropagation(model, ctx.updates)
  })
  return true
}

/**
 * REVIT JOINS round (2026-10-01) — a MOVED wall: it translates rigidly, partners slide
 * along their own lines (never turn) and the moved wall's own joined end trims to the
 * same point; partners in `movingSet` travel with the gesture. Now one call into the
 * shared follow engine (`followJoins`, 2026-10-08), which also fixed the collinear
 * partner this rule used to drag askew.
 */
function planTranslateJoins(
  model: ModelDoc, elementId: ElementId, oldPath: Path2D, newPath: Path2D, movingSet: Set<ElementId> | null,
  disjoin = false,
): { own: Path2D; updates: Map<ElementId, Path2D>; disjoin: { a: JoinEnd; b: JoinEnd }[] } | null {
  const ctx: FollowCtx = {
    model, rev: reverseJoinIndex(model), updates: new Map(), visited: new Set([elementId]),
    moving: new Set(movingSet ?? []),
    ...(disjoin ? { disjoin: [] } : {}),
  }
  const own = followJoins(ctx, elementId, oldPath, newPath, true)
  if (!own) return null
  // a disjoined partner stays exactly where it was
  for (const dj of ctx.disjoin ?? []) ctx.updates.delete(dj.b.id)
  return { own, updates: ctx.updates, disjoin: ctx.disjoin ?? [] }
}

/** GROUP MOVE round 3: what a move had to do / couldn't do (translateElement fills it) */
export interface MoveReport { disjoined: number; pinned: number; blocked: number }
export function newMoveReport(): MoveReport { return { disjoined: 0, pinned: 0, blocked: 0 } }

/** clear the join between two wall ends, both directions (Revit's Disjoin) */
function clearJoinBetween(model: ModelDoc, a: JoinEnd, b: JoinEnd): void {
  const { elements } = maps(model)
  for (const [x, y] of [[a, b], [b, a]] as const) {
    const pm = elements.get(x.id)?.get('params') as Y.Map<unknown> | undefined
    const ref = pm?.get(wallJoinField(x.end)) as WallJoinRef | null | undefined
    if (pm && ref && typeof ref === 'object' && ref.targetId === y.id) pm.delete(wallJoinField(x.end))
  }
}

// ---------------------------------------------------------------------------
// ROTATE + MIRROR (docs/MODELER.md "ROTATE + MIRROR pet-palette commands")
// ---------------------------------------------------------------------------

/**
 * Which instance param encodes "which side of my own freshly-derived left-normal I'm
 * on" for an element that owns its plan geometry — toggled on MIRROR only. Proof (see
 * docs/MODELER.md): for any tangent v and any reflection R, rotate90(R·v) = −R(rotate90
 * (v)) — the left-normal recomputed fresh from a REFLECTED path is always the NEGATION
 * of the correctly-mirrored original normal. Every one of these flags exists precisely
 * to pick a side relative to that normal, so each must flip to cancel the negation and
 * land the true mirror image. Floor/ceiling/roof have no such flag (their assemblies
 * stack in Z, not off a path side) — mirroring them needs nothing extra.
 */
const MIRROR_SIDE_FLAG: Partial<Record<CategoryId, string>> = {
  wall: 'flipped',
  // SKETCHED STAIRS: the sketch is stored in the stair's frame — a mirror flips its sides
  stair: 'sketchMirrored',
}

/** Same role as MIRROR_SIDE_FLAG, but for a HOSTED CHILD whose own world position rides
 *  the HOST's left-normal (door swing) — toggled on the child when its HOST is the one
 *  being mirrored (never on a rotate; rotation doesn't flip the normal). Windows have no
 *  facing param — the plan symbol is 3 lines symmetric about the centerline, so nothing
 *  to flip; hand stays for every hosted category (proof in docs/MODELER.md: hand picks
 *  an END by arc-length station, which a mirror preserves exactly, so flipping it too
 *  would put the hinge/track at the WRONG jamb — the "re-handed original" failure mode
 *  the round explicitly calls out). Casework moved off this path entirely in the
 *  FREESTANDING round (2026-08-19) — it's no longer a hosted child, so it mirrors
 *  through the real geometric path (mirrorElement) like any anchor category.
 */
function flipCarriedMirrorFlag(opm: Y.Map<unknown>, category: CategoryId | undefined): void {
  if (category === 'door') toggleChoice(opm, 'facing', 'in', 'out')
  // window: nothing to flip
}

function toggleBool(pm: Y.Map<unknown>, key: string): void {
  pm.set(key, !(pm.get(key) as boolean))
}
function toggleChoice(pm: Y.Map<unknown>, key: string, a: string, b: string): void {
  const v = pm.get(key)
  if (v === a) pm.set(key, b)
  else if (v === b) pm.set(key, a)
}
function normDeg(deg: number): number {
  let v = deg % 360
  if (v > 180) v -= 360
  if (v <= -180) v += 360
  return v
}

/**
 * Re-anchor every opening hosted in `elementId` across a RIGID transform of its own path
 * (rotate/mirror — a big move, unlike a shape edit): project the OLD world anchor
 * through the SAME transform, then find its station on the NEW path. Unlike
 * `rehostOpeningsForPathChange` (which projects the OLD anchor onto the NEW path
 * directly — correct only for a small local shape edit), this stays correct for an
 * arbitrary rotation/reflection because it transforms the anchor itself first.
 */
function rehostOpeningsForRigidTransform(
  model: ModelDoc,
  elementId: ElementId,
  oldPath: Path2D,
  newPath: Path2D,
  transformPt: (pt: Pt2) => Pt2,
  mirror: boolean,
): void {
  const openings = hostedElements(model, elementId)
  if (!openings.length) return
  const oldPts = flattenPathPts(oldPath)
  const newPts = flattenPathPts(newPath)
  if (oldPts.length < 2 || newPts.length < 2) return
  const { elements } = maps(model)
  for (const op of openings) {
    const s = typeof op.params.hostOffsetFt === 'number' ? op.params.hostOffsetFt : 0
    const oldAnchor = ops.polylinePointAt(oldPts, s).pt
    const sNew = ops.projectToPolyline(newPts, transformPt(oldAnchor)).s
    const oem = elements.get(op.id)
    if (!oem) continue
    const opm = oem.get('params') as Y.Map<unknown>
    opm.set('hostOffsetFt', sNew)
    if (mirror) flipCarriedMirrorFlag(opm, op.category)
  }
}

/**
 * Closed loops stored BESIDE a boundary that must ride it through every move, rotate and
 * mirror: floor/ceiling openings (`boundaryHoles`, which used to stay behind when the floor
 * moved) and a roof's vertical cuts (`verticalCuts`, 2026-10-08).
 */
const BOUNDARY_RIDER_KEYS = ['boundaryHoles', 'verticalCuts'] as const
function moveRiderLoops(pm: Y.Map<unknown>, key: string, fn: (p: Path2D) => Path2D): void {
  if (key !== 'boundary') return
  for (const k of BOUNDARY_RIDER_KEYS) {
    const v = pm.get(k) as Path2D[] | null | undefined
    if (!Array.isArray(v) || !v.length) continue
    pm.set(k, v.map((p) => (p && Array.isArray(p.nodes) ? fn(p) : p)))
  }
}

interface RigidTransformOpts {
  transformPt: (pt: Pt2) => Pt2
  transformPath: (p: Path2D) => Path2D
  /** anchor categories (object/column: single-node path + rotationDeg) — the new
   *  rotationDeg given the current value; absent categories are untouched */
  newRotationDeg: (oldDeg: number) => number
  mirror: boolean
}

function applyRigidTransform(model: ModelDoc, elementId: ElementId, opts: RigidTransformOpts): boolean {
  const em = maps(model).elements.get(elementId)
  if (!em) return false
  if (em.get('category') === 'body') {   // sketch geometry — see translateElement
    return moveBodyParts(model, elementId, null, (p) => {
      const q = opts.transformPt([p.x, p.y])
      return { x: q[0], y: q[1], z: p.z }
    }).ok
  }
  const pm = em.get('params') as Y.Map<unknown> | undefined
  if (!pm) return false
  if (pm.get('pinned') === true) return false   // MODIFY PHASE-2 (Pin)
  const category = em.get('category') as CategoryId | undefined
  const key = geomKeyFor(pm, category)
  if (!key) return false
  const oldPath = pm.get(key) as Path2D | undefined
  if (!oldPath || !Array.isArray(oldPath.nodes)) return false
  const newPath = opts.transformPath(oldPath)
  // WALL JOINS: rotate/mirror moves both ends at once — same dry-run-then-apply plan as
  // translateElement/writePath (refuses, mutating nothing, if it would collapse a joined
  // wall). No auto-detect here either (v1 scope).
  const isWall = category === 'wall' && key === 'path'
  const joinPlan = isWall ? planWallJoinPropagation(model, elementId, newPath) : new Map<ElementId, Path2D>()
  if (isWall && joinPlan === null) return false
  transact(model, () => {
    pm.set(key, newPath)
    moveRiderLoops(pm, key, opts.transformPath)
    if (pm.get('rotationDeg') !== undefined) {
      pm.set('rotationDeg', normDeg(opts.newRotationDeg((pm.get('rotationDeg') as number) || 0)))
    }
    const declared = pathParamKeyFor(category as CategoryId)
    if (declared != null && declared === key) {
      rehostOpeningsForRigidTransform(model, elementId, oldPath, newPath, opts.transformPt, opts.mirror)
    }
    if (opts.mirror) {
      const flag = MIRROR_SIDE_FLAG[category as CategoryId]
      if (flag) toggleBool(pm, flag)
    }
    if (isWall) applyJoinPropagation(model, joinPlan!)
  })
  return true
}

/**
 * Rotate a path/anchor element's whole geometry about `center` by `deg` (CCW). Path
 * categories (wall, floor/ceiling boundary, beam/railing/ramp/stair) rotate their stored
 * nodes; anchor categories (object, column, and casework since the FREESTANDING round)
 * rotate the single anchor node AND add `deg` to `rotationDeg` so the facing tracks the
 * turn. Hosted categories (door/window/fixture) have no plan geometry of their own —
 * they ride the host — and simply no-op (false): the pet palette never offers Rotate
 * for them.
 */
export function rotateElement(
  model: ModelDoc, elementId: ElementId, center: Pt2, deg: number,
  /** DINING CHAIRS: the other elements turning in the same gesture */
  movingSet: Set<ElementId> | null = null,
): boolean {
  let ok = false
  transact(model, () => {
    ok = applyRigidTransform(model, elementId, {
      transformPt: (pt) => ops.rotatePoint(pt, center, deg),
      transformPath: (p) => ops.rotatePath(p, center, deg),
      newRotationDeg: (old) => old + deg,
      mirror: false,
    })
    // DINING CHAIRS BY COUNT: a table turns its chairs with it
    if (ok && isDiningTable(model, elementId)) for (const c of chairsOfTable(model, elementId)) if (!movingSet?.has(c)) rotateElement(model, c, center, deg, movingSet)
  })
  return ok
}

/**
 * Reflect a path/anchor element's whole geometry across the line through `axisOrigin`
 * in direction `axisDir`. Path categories reflect their stored nodes (closed boundaries
 * also reverse node order — see `pathOps.mirrorPath`); anchor categories reflect the
 * anchor point and recompute `rotationDeg` from the reflected FACING vector (the same
 * (−sin R, cos R) convention the rotate grip uses), which is what makes the anchor's
 * drawn footprint an actual mirror image instead of a rotated one. A host's own hosted
 * openings are re-anchored (hostOffsetFt) and their carried mirror flag flips — see
 * `flipCarriedMirrorFlag`. Hosted categories (door/window/fixture) alone have no path
 * of their own; use `mirrorHostedInPlace` for those.
 */
export function mirrorElement(
  model: ModelDoc, elementId: ElementId, axisOrigin: Pt2, axisDir: Pt2,
  /** DINING CHAIRS: the other elements mirroring in the same gesture */
  movingSet: Set<ElementId> | null = null,
): boolean {
  let ok = false
  transact(model, () => {
    ok = mirrorOne(model, elementId, axisOrigin, axisDir)
    // DINING CHAIRS BY COUNT: a table mirrors its chairs with it
    if (ok && isDiningTable(model, elementId)) for (const c of chairsOfTable(model, elementId)) if (!movingSet?.has(c)) mirrorElement(model, c, axisOrigin, axisDir, movingSet)
  })
  return ok
}

function mirrorOne(model: ModelDoc, elementId: ElementId, axisOrigin: Pt2, axisDir: Pt2): boolean {
  return applyRigidTransform(model, elementId, {
    transformPt: (pt) => ops.mirrorPoint(pt, axisOrigin, axisDir),
    transformPath: (p) => ops.mirrorPath(p, axisOrigin, axisDir),
    newRotationDeg: (old) => {
      const rad = (old * Math.PI) / 180
      const f = ops.mirrorPoint([-Math.sin(rad), Math.cos(rad)], [0, 0], axisDir)
      return (Math.atan2(-f[0], f[1]) * 180) / Math.PI
    },
    mirror: true,
  })
}

/**
 * Standalone hosted-element mirror (door/window selected ALONE, no host in the
 * operation) — "its only meaningful mirror" (docs/MODELER.md): the element can't move
 * off its host, so there is no axis to reflect across. This is a literal handing flip in
 * place — hand L↔R (every hosted category that has one) and, for doors specifically,
 * facing in↔out (the schedule-level "flip this door's hardware" convention — distinct
 * from the CARRIED-mirror rule in `flipCarriedMirrorFlag`, which leaves hand alone
 * because there IS a host transform to derive the true mirror from). Casework no longer
 * reaches this function (FREESTANDING round, 2026-08-19) — `elements.ts`'s mirror
 * dispatch is keyed on `hostId` presence, and a freestanding cabinet never has one, so
 * it always takes the real `mirrorElement` branch instead.
 */
export function mirrorHostedInPlace(model: ModelDoc, elementId: ElementId): boolean {
  const em = maps(model).elements.get(elementId)
  if (!em) return false
  const pm = em.get('params') as Y.Map<unknown> | undefined
  if (!pm) return false
  if (pm.get('pinned') === true) return false   // MODIFY PHASE-2 (Pin)
  const category = em.get('category') as CategoryId | undefined
  transact(model, () => {
    if (pm.get('hand') !== undefined) toggleChoice(pm, 'hand', 'L', 'R')
    if (category === 'door' && pm.get('facing') !== undefined) toggleChoice(pm, 'facing', 'in', 'out')
  })
  return true
}

/** Per-edge attribute merge (Phase 3 roofs — slope/overhang/gable on a boundary edge). */
export function setEdgeAttrs(
  model: ModelDoc,
  elementId: ElementId,
  paramKey: string,
  edge: EdgeRef,
  attrs: Record<string, number | boolean | string | null>,
): boolean {
  const r = readPath(model, elementId, paramKey)
  if (!r) return false
  // EAVES IN A PHASE (eavePhase.ts): with the roof's "Eaves as" switch on a later phase, eave
  // attrs on an edge are recorded as that phase's change, never written onto the existing edge
  const eavePh = eaveEditPhaseFor(model.ydoc, elementId as string)
  const keys = Object.keys(attrs)
  if (eavePh && keys.length && keys.every(isRoofEaveEdgeKey) && changePhaseFor(model, elementId, 'overhangFt') === eavePh) {
    const nodeId = (edge as { fromNodeId?: string }).fromNodeId
    if (!nodeId || !r.path.nodes.some((n) => n.id === nodeId)) return false
    transact(model, () => {
      let ch = normalizePhaseChanges(r.pm.get(PHASE_CHANGES_KEY))
      for (const k of keys) ch = withEdgeChange(ch, eavePh, paramKey, nodeId, k, attrs[k] === null ? UNSET : attrs[k] as ParamValue)
      if (ch.length) r.pm.set(PHASE_CHANGES_KEY, ch)
      else r.pm.delete(PHASE_CHANGES_KEY)
    })
    return true
  }
  let next = ops.setEdgeAttrs(r.path, edge, attrs)
  if (!next) return false
  // SLOPED CEILING round: a ceiling has ONE slope-defining edge — turning it on for
  // this edge turns it off for every other edge (Revit's single Defines Slope line)
  const slabCat = maps(model).elements.get(elementId)?.get('category')
  if (attrs.ceilingSlope === true && (slabCat === 'ceiling' || slabCat === 'floor')) {
    const keep = (edge as { fromNodeId?: string }).fromNodeId
    next = {
      ...next,
      nodes: next.nodes.map((nd) => {
        if (nd.id === keep || !nd.edge?.ceilingSlope) return nd
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ceilingSlope: _off, slopeRise: _r, ...rest } = nd.edge
        const { edge: _e, ...bare } = nd
        return Object.keys(rest).length ? { ...bare, edge: rest } : bare
      }),
    }
  }
  writePath(model, elementId, r.pm, paramKey, r.path, next)
  return true
}

/** The one-time JOIN CLEANUP a model gets on open (a settings flag, so a join the user
 *  deliberately cleared later is never re-linked by a later open). null = already done. */
export function repairWallJoinsOnce(model: ModelDoc): { cleared: number; mirrored: number; linked: number } | null {
  const settings = maps(model).settings
  if (settings.get('wallJoinRepairV1') === true) return null
  let out: { cleared: number; mirrored: number; linked: number } | null = null
  transact(model, () => {
    out = repairWallJoins(model)
    settings.set('wallJoinRepairV1', true)
  })
  return out
}
